import { useRefresh, useStore } from 'react-admin';
import { Checkbox, Grid2 as Grid, Tooltip } from '@mui/material';
import { ReactElement, useEffect, useMemo } from 'react';
import { roleDefinitions } from '../DataProvider/AuthProvider';
import { WarningAmberRounded, WarningRounded } from '@mui/icons-material';
import { match } from 'ts-pattern';
import { not } from '../utils';

const knownPermissions = Object.keys(roleDefinitions);

const PERMISSION_NOT_GRANTED = 'This permission has not been granted to your user';
const PERMISSION_UNKNOWN =
  "This permission has been granted to your user, but this version of the application doesn't know how to handle it";

export default function AssumedPermissionsPreferencesEditor(): ReactElement {
  const [assumed, setAssumed] = useStore<string[] | undefined>(
    'preferences.assumedPermissions',
    undefined,
  );
  const refresh = useRefresh();

  const userPermissions: string[] = useMemo(() => {
    const permissions = localStorage.getItem('permissions');
    return permissions ? JSON.parse(permissions) : [];
  }, []);

  useEffect(refresh, [assumed, refresh]);

  useEffect(() => {
    if (assumed === undefined && userPermissions.length > 0) {
      setAssumed(userPermissions);
    }

    // Find any revoked permissions and remove them from the assumed permissions
    const revoked =
      assumed?.filter((permission: string) => !userPermissions.includes(permission)) ?? [];

    if (revoked.length > 0) {
      setAssumed(assumed?.filter((permission: string) => !revoked.includes(permission)));
    }
  }, [assumed, setAssumed, userPermissions]);

  const allPermissions = [...new Set([...userPermissions, ...knownPermissions])];

  function renderCheckbox(permission: string): ReactElement {
    const tooltip = match(permission)
      .when(not(userPermissions.includes.bind(userPermissions)), () => PERMISSION_NOT_GRANTED)
      .when(not(knownPermissions.includes.bind(knownPermissions)), () => PERMISSION_UNKNOWN)
      .otherwise(() => undefined);

    const userHasPermission = userPermissions.includes(permission);
    const permissionIsApplied = assumed?.includes(permission) ?? false;
    const permissionIsKnown = knownPermissions.includes(permission);

    return (
      <Grid key={permission} size={{ xs: 12, xl: 4 }}>
        <Tooltip title={tooltip}>
          <span>
            <Checkbox
              checked={permissionIsApplied}
              checkedIcon={!permissionIsKnown ? <WarningRounded /> : undefined}
              color={permissionIsKnown ? 'primary' : 'warning'}
              disabled={!userHasPermission}
              icon={!permissionIsKnown ? <WarningAmberRounded /> : undefined}
              onChange={(e) =>
                e.target.checked
                  ? setAssumed([...new Set(assumed).add(permission)])
                  : setAssumed(assumed?.filter((ap: string) => ap !== permission))
              }
            />
            {permission}
          </span>
        </Tooltip>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid container size={12}>
        <Grid size={12}>
          <b>Special Permissions</b>
        </Grid>
        {renderCheckbox('admin:api')}
      </Grid>
      <Grid container size={{ xs: 12, xl: 6 }}>
        <Grid size={12}>
          <b>Read Permissions</b>
        </Grid>
        {allPermissions.filter((p) => p.startsWith('read')).map(renderCheckbox)}
      </Grid>
      <Grid container size={{ xs: 12, xl: 6 }}>
        <Grid size={12}>
          <b>Write Permissions</b>
        </Grid>
        {allPermissions.filter((p) => p.startsWith('write')).map(renderCheckbox)}
      </Grid>
    </Grid>
  );
}
